/**
 * 1. Update the view with a batch's details
 * 2. Make edits or not
 * 3. Save the batch 
 * 4. Delete batch
 */
import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { BatchService } from 'src/core/services/batch.service';
import { DirtinessService } from 'src/core/services/dirtiness.service';
import { LoaderService } from 'src/core/services/loader.service';
import { PrescriptionService } from 'src/core/services/prescription.service';
import { UtilsService } from 'src/core/services/utils.service';
import { PermissionsService } from 'src/core/services/permissions.service';
import { Batch, BatchCreatePayload, BatchStatus, BatchItem } from 'src/interfaces/batch';
import { Medicine } from 'src/interfaces/medicine';
import { MedicineDosagePackageType } from 'src/interfaces/medicine-dosage-package-type';
import { MedicineDosageType } from 'src/interfaces/medicine-dosage-type';
import { Order } from 'src/interfaces/order';
import { PackageType } from 'src/interfaces/package-type';

@Component({
    selector: 'app-batch-view',
    templateUrl: './batch-view.component.html',
    styleUrls: ['./batch-view.component.scss'],
    standalone: false
})
export class BatchViewComponent implements OnInit {

  batch: Batch | null = null;
  medicinePage: number = 1;
  medicineLimit: number = 30;
  medicine: Medicine[] = [];
  medicineDosageTypes: MedicineDosageType[] = [];
  batchMedicine: Medicine | null = null;
  batchMedicineDosageType: MedicineDosageType | null = null;
  selectedMedicineDosageType: MedicineDosageType | null = null;
  batchMedicineDosagePackageType: MedicineDosagePackageType | null = null;
  selectedMedicineDosagePackageType: MedicineDosagePackageType | null = null;
  batchPackageType: PackageType | null = null;
  medicineDosagePackageTypes: MedicineDosagePackageType[] = [];

  batchDetailsForm = this.formBuilder.group({
    batchNumber: [{ value: '', disabled: true }, Validators.required],
    medicine: [{ value: '', disabled: true }, Validators.required],
    quantity: [{ value: '', disabled: true }, Validators.required],
    statusId: ['', Validators.required],
    medicineDosageType: [{ value: null, disabled: true }, Validators.required],
    packageTypeId: [{ value: '', disabled: true }, Validators.required],
    medicineDosagePackageTypeId: ['', Validators.required],
    additional_information: [''],
    expiryDate: ['', Validators.required]
  });

  filteredMedicine: Observable<Medicine[]>;
  isLoading: boolean = false;

  batchStatuses: BatchStatus[] = [];

  disabled: boolean = true;

  editMode: boolean = false;

  fulfilledOrders: Order[];

  constructor(
    private _prescriptionService: PrescriptionService,
    private formBuilder: UntypedFormBuilder,
    private loaderService: LoaderService,
    private route: ActivatedRoute,
    private router: Router,
    private _dirtinessService: DirtinessService,
    private _batchService: BatchService,
    public utilService: UtilsService,
    public readonly perm: PermissionsService
  ) { }

  ngOnInit(): void {

    this
      .loaderService
      .isLoading
      .subscribe(isLoading => {
        this.isLoading = isLoading;
      });

    this
      ._prescriptionService
      .getFormData(['batch_status'])
      .subscribe(data => {
        this.batchStatuses = data.batch_status;
      });

    this
      .route
      .data
      .subscribe(data => {
        this.batch = data.batch;
        (async () => {
          if (this.batch)
            await this.updateView(this.batch);
          this._dirtinessService.setDirty(false);
        })();
      });

    this
      .batchDetailsForm
      .controls['statusId']
      .valueChanges
      .subscribe(value => {
        this._dirtinessService.setDirty(true);
      });

    this
      .batchDetailsForm
      .controls['quantity']
      .valueChanges
      .subscribe(value => {
        this._dirtinessService.setDirty(true);
      });

  }

  async getMedicine(value: string) {
    this.medicine = await this
      ._prescriptionService
      .getMedicine(value, this.medicinePage, this.medicineLimit);
  }

  medicineSelected(event: MatAutocompleteSelectedEvent) {
    this.batchMedicine = event.option.value;

    if (!this.batchMedicine) return;

    this.batchDetailsForm.patchValue({
      medicine: this.batchMedicine.name,
    });
    this.getDosageTypes(this.batchMedicine.id);
  }


  async getDosageTypes(medicineId: number) {

    this.medicineDosageTypes = await this._prescriptionService
      .getMedicineDosageTypes(medicineId)
      .toPromise();

  }

  async _getMedicineDosageTypes(medicineId: number) {

    return await this._prescriptionService
      .getMedicineDosageTypes(medicineId)
      .toPromise();

  }

  async getMedicineDosagePackageTypes(event: MatSelectChange) {
    const dosageTypeId = parseInt(event.value);
    if (!this.batchMedicine) return;
    this.medicineDosagePackageTypes = await this._getMedicineDosagePackageTypes(this.batchMedicine.id, dosageTypeId);
  }

  async _getMedicineDosagePackageTypes(medicineId: number, dosageTypeId: number) {
    return await this._prescriptionService
      .getMedicineDosagePackageTypes(medicineId, dosageTypeId)
      .toPromise();
  }

  submitData() {
    this.save();
  }

  async save() {
    if (!this.batch) return;

    const tzoffset = (new Date()).getTimezoneOffset() * 60000;
    const expiryDate = (new Date(this.batchDetailsForm.value.expiryDate - tzoffset)).toISOString();

    const payload: BatchCreatePayload = {
      quantity: this.batchDetailsForm.value.quantity,
      batch_status_id: this.batchDetailsForm.value.statusId,
      medicine_dosage_package_type_id: this.batchDetailsForm.value.packageTypeId,
      batch_number: this.batchDetailsForm.value.batchNumber,
      expiry_date: expiryDate,
      additional_information: this.batchDetailsForm.value.additional_information
    }

    const success = await this
      ._prescriptionService
      .editBatch(payload, this.batch.id);

    if (success) {
      this.batch = await this._prescriptionService
        .getBatch(this.batch.id);
      if (this.batch)
        await this.updateView(this.batch);
    }
  }

  async updateView(batch: Batch) {


    // Prepare fulfilled orders 
    for (const order of batch.fulfilled_orders) {
      // Filter order items 
      order.order_items = order.order_items.filter(orderItem => {
        return orderItem.batch_items ? orderItem.batch_items.some((item: BatchItem) => {

          const itemBatchNumber: string | null = item.barcode ? item.barcode.split('-')[0] : null;

          if (!itemBatchNumber) return false;

          return itemBatchNumber === batch.batch_number;

        }) : false
      })


    }

    this.fulfilledOrders = batch.fulfilled_orders;

    this.batchMedicine = batch
      .medicine_dosage_type
      .medicine;

    this.batchMedicineDosageType = batch.medicine_dosage_type
    this.batchMedicineDosagePackageType = batch
      .medicine_dosage_type
      .medicine_dosage_package_type
      .filter(type =>  type.id === batch.medicine_dosage_package_type_id)[0];

    this.batchPackageType = this.batchMedicineDosagePackageType.package_type;

    this.medicineDosageTypes = await this._getMedicineDosageTypes(this.batchMedicine.id);

    const selectedMedicineDosageType = await this.medicineDosageTypes
      .find(type => {
        if (!this.batchMedicineDosageType) return false;
        return this.batchMedicineDosageType.id === type.id;
      });

    this.selectedMedicineDosageType = selectedMedicineDosageType ? selectedMedicineDosageType : null;

    this.medicineDosagePackageTypes = await this._getMedicineDosagePackageTypes(this.batchMedicine.id, this.batchMedicineDosageType.id);

    this.batchDetailsForm.patchValue({
      medicine: this.batchMedicine.name,
      quantity: batch.quantity_produced,
      batchNumber: batch.batch_number,
      statusId: batch.batch_status_id,
      medicineDosageType: this.selectedMedicineDosageType,
      packageTypeId: this.batchMedicineDosagePackageType.id,
      medicineDosagePackageTypeId: this.batchMedicineDosagePackageType.id,
      additional_information: batch.additional_information,
      expiryDate: batch.expiry_date ? new Date(batch.expiry_date) : null
    });

    this._dirtinessService.setDirty(false);

  }

  async deleteBatch() {

  }

  async printLabels() {
    if (!this.batch) return;
    const success: boolean = await this._batchService
      .openBatchPrintDialog(
        "Print labels",
        "Click the 'Print' button to start printing.",
        "label",
        this.batch
      );
  }

  async printBarcode() {
    if (!this.batch) return;
    const success: boolean = await this._batchService
      .openBatchPrintDialog(
        "Print barcodes",
        "Click the 'Print' button to start printing.",
        "barcode",
        this.batch
      );
  }

  // Navigate back to batch list preserving query params
  backToList() {
    const qp = this.route.snapshot.queryParamMap;
    const batchNumber = qp.get('batchNumber') || undefined;
    const medicineId = qp.get('medicineId') || undefined;
    const batchStatusId = qp.get('batchStatusId') || undefined;
    this.router.navigate(["/portal/batches"], {
      queryParams: { batchNumber, medicineId, batchStatusId }
    });
  }

}
